import { useCallback, useContext, useMemo } from 'react';

import { fg } from '@confluence/feature-gating';
import { AudienceSegmentationContext } from '@confluence/custom-sites-extensions/entry-points/AudienceSegmentationContext';

import type { LinkCardsParameters } from '../linkCardsTypes';

type Props = {
	isInViewMode: boolean;
} & Pick<LinkCardsParameters, 'cards' | 'isSegmentationShown'>;

type ReturnType = {
	filteredCards: LinkCardsParameters['cards'];
	getShouldShowSegmentationMessage: (audienceSegmentationGroupIds: string[] | undefined) => boolean;
};

export const useAudienceSegmentation = ({
	isSegmentationShown,
	cards,
	isInViewMode,
}: Props): ReturnType => {
	const { isTargetAudience, isCompanyHub } = useContext(AudienceSegmentationContext);

	const shouldApplySegmentation = useMemo(
		() => isCompanyHub && isSegmentationShown,
		[isCompanyHub, isSegmentationShown],
	);

	// filter out cards for segmentation, but if it's in edit mode, company hub audience segmentation is disabled or if card segmentation is disabled, no filtering
	const filteredCards = useMemo(
		() =>
			isInViewMode && shouldApplySegmentation && fg('company_hub_audience_segmentation')
				? cards.filter((card) => isTargetAudience(card.audienceSegmentationGroupIds))
				: cards,
		[cards, isInViewMode, shouldApplySegmentation, isTargetAudience],
	);

	const getShouldShowSegmentationMessage = useCallback(
		(audienceSegmentationGroupIds) =>
			shouldApplySegmentation &&
			audienceSegmentationGroupIds && // only show message if audience group is specified.
			isTargetAudience(audienceSegmentationGroupIds) &&
			fg('company_hub_cards_segmentation_indicator'),
		[shouldApplySegmentation, isTargetAudience],
	);

	return {
		filteredCards,
		getShouldShowSegmentationMessage,
	};
};
